import React, { useState } from "react";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { 
  ChevronRight, 
  MessageSquare, 
  FileText, 
  Megaphone, 
  Target, 
  Monitor, 
  Crosshair, 
  Diamond, 
  Headphones, 
  Cpu, 
  Settings, 
  LayoutGrid, 
  Swords,
  CornerDownRight 
} from "lucide-react";
import LastPostMember from "./LastPostMember";
import { useT } from "../i18n";

function getCategoryIconAndDesc(id) {
  const norm = (id || "").toLowerCase();
  if (norm.includes("anun") || norm.includes("importan") || norm.includes("admin")) return { icon: Megaphone, defaultDesc: "Management, dezvoltare, regulament și administrarea comunității", fallbackColor: "#BF40FF" };
  if (norm.includes("clan") || norm.includes("war") || norm.includes("esport")) return { icon: Swords, defaultDesc: "Turnee oficiale, meciuri 5v5, divizii și provocări de clan", fallbackColor: "#FF2A2A" };
  if (norm.includes("cs16") || norm.includes("1.6")) return { icon: Target, defaultDesc: "Servere oficiale, pluginuri, addons, resurse și discuții CS 1.6", fallbackColor: "#00F0FF" };
  if (norm.includes("cs2") || norm.includes("counter-strike 2")) return { icon: Monitor, defaultDesc: "Servere, matchmaking, tactici, ghiduri și competiții CS2", fallbackColor: "#FF4F00" };
  if (norm.includes("vip") || norm.includes("market") || norm.includes("shop")) return { icon: Diamond, defaultDesc: "Beneficii premium, shop IDG Coins, avatar frames și tranzacții", fallbackColor: "#FFD700" };
  if (norm.includes("support") || norm.includes("suport")) return { icon: Headphones, defaultDesc: "Cereri, reclamații, asistență și contact cu staff-ul comunității", fallbackColor: "#39FF14" };
  if (norm.includes("general") || norm.includes("comunitat")) return { icon: Cpu, defaultDesc: "Discuții libere, media, divertisment și interacțiune între membri", fallbackColor: "#10B981" };
  return { icon: LayoutGrid, defaultDesc: "Subiecte și discuții aferente categoriei", fallbackColor: "var(--tf-primary)" };
}

export default function ForumCategoryList({ boards: apiBoards = [], isLoading, settings = {}, categoriesList = [] }) {
  const t = useT();
  const [collapsedCategories, setCollapsedCategories] = useState({});

  const toggleCategory = (id) => {
    setCollapsedCategories(prev => ({ ...prev, [id]: !prev[id] }));
  };

  if (isLoading) {
    return (
      <div className="space-y-4 animate-pulse">
        {[1, 2, 3].map(i => (
          <div key={i} className="bg-[#0b0e15] border border-white/10 h-44" />
        ))}
      </div>
    );
  }

  const boards = apiBoards || [];
  
  // Determine categories to display
  let gamesWithBoards = [];
  
  const grouped = {};
  // First, add all predefined categories
  if (categoriesList && categoriesList.length > 0) {
    categoriesList.forEach(c => {
      grouped[c.id] = { ...c };
    });
  }
  // Then add any implicit ones from boards
  boards.forEach((b) => {
    if (b.parent_id) return;
    const gId = b.game || "general";
    if (!grouped[gId]) {
      grouped[gId] = { id: gId, label: gId.toUpperCase() };
    }
  });
  gamesWithBoards = Object.values(grouped).sort((a, b) => {
    const idxA = categoriesList.findIndex(x => x.id === a.id);
    const idxB = categoriesList.findIndex(x => x.id === b.id);
    if (idxA !== -1 && idxB !== -1) return idxA - idxB;
    if (idxA !== -1) return -1;
    if (idxB !== -1) return 1;
    return a.label.localeCompare(b.label);
  });

  return (
    <div className="space-y-6">
      {gamesWithBoards.map((game) => {
        const activeBoards = boards.filter((b) => b.game === game.id && !b.parent_id);
        const { defaultDesc, fallbackColor } = getCategoryIconAndDesc(game.id);
        
        let CatIcon = getCategoryIconAndDesc(game.id).icon;
        let catIconIsUrl = false;
        if (game.icon) {
          if (game.icon.startsWith('http') || game.icon.startsWith('/')) {
            catIconIsUrl = true;
          } else if (Icons[game.icon]) {
            CatIcon = Icons[game.icon];
          }
        }
        
        const color = game.color || fallbackColor;
        const bgImage = game.bg_image || null;
        const isCollapsed = !!collapsedCategories[game.id];

        return (
          <section 
            key={game.id} 
            className="bg-[#090c12] border border-[#151924] overflow-hidden shadow-2xl transition-all"
            data-testid={`forum-section-${game.id}`}
          >
            {/* 1. SHARP TACTICAL CATEGORY HEADER */}
            <div 
              className="bg-[#06080d] border-b border-[#141824] p-3.5 sm:p-4 flex items-center justify-between cursor-pointer select-none transition-colors hover:bg-[#0a0d14] relative overflow-hidden"
              onClick={() => toggleCategory(game.id)}
            >
              {/* Subtle accent line on top */}
              <div 
                className="absolute top-0 left-0 right-0 h-[2px]"
                style={{ backgroundColor: color }}
              />

              {/* Background artwork or gradient */}
              {bgImage ? (
                <div className="absolute inset-0 z-0 pointer-events-none">
                  <img src={bgImage} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover opacity-15" />
                  <div className="absolute inset-0 bg-gradient-to-r from-[#06080d] via-[#06080d]/80 to-transparent" />
                </div>
              ) : (
                <div 
                  className="absolute inset-0 opacity-10 pointer-events-none"
                  style={{ background: `linear-gradient(90deg, ${color} 0%, transparent 60%)` }}
                />
              )}
              
              {/* Left Side: Badge + Title + Subtitle */}
              <div className="flex items-center relative z-10">
                <div 
                  className="w-10 h-10 flex items-center justify-center text-black font-black shrink-0 mr-3.5 shadow-md"
                  style={{ backgroundColor: color }}
                >
                  {catIconIsUrl ? (
                    <img src={game.icon} alt={game.label} loading="lazy" decoding="async" className="w-6 h-6 object-contain" />
                  ) : (
                    <CatIcon className="w-5 h-5 text-black stroke-[2.5]" />
                  )}
                </div>
                
                <div>
                  <h2 className="font-chakra text-base sm:text-lg font-black uppercase tracking-wider text-white">
                    {game.label}
                  </h2>
                  <p className="text-[11px] text-zinc-400 font-mono mt-0.5">
                    // {game.desc || defaultDesc}
                  </p>
                </div>
              </div>

              {/* Right Side: Tactical diagonal bars & Collapse Arrow */}
              <div className="flex items-center gap-3 relative z-10">
                <div className="hidden sm:flex gap-1 opacity-70">
                  {[1, 2, 3].map(i => (
                    <div 
                      key={i}
                      className="w-1.5 h-6"
                      style={{ 
                        backgroundColor: color,
                        transform: 'skewX(-20deg)'
                      }}
                    />
                  ))}
                </div>

                <div className="w-7 h-7 bg-black/60 border border-[#181e2b] flex items-center justify-center text-zinc-400 hover:text-white transition-colors">
                  <ChevronRight className={`w-4 h-4 transition-transform duration-300 ${isCollapsed ? '' : 'rotate-90'}`} />
                </div>
              </div>
            </div>

            {/* 2. CATEGORY BOARDS LIST */}
            {!isCollapsed && (
              <div>
                {/* Table Header Bar */}
                <div className="grid grid-cols-12 gap-4 px-4 py-2 bg-black/40 border-b border-[#121622] text-[10px] font-mono font-bold text-zinc-500 uppercase tracking-widest">
                  <div className="col-span-12 md:col-span-8">
                    {t("forums.board", "SECȚIUNE FORUM")}
                  </div>
                  <div className="col-span-4 hidden md:block">
                    {t("forums.last_post", "ULTIMA ACTIVITATE")}
                  </div>
                </div>

                {/* Boards Rows */}
                <div className="divide-y divide-[#10141e]">
                  {activeBoards.length === 0 ? (
                    <div className="py-8 text-center text-zinc-500 text-xs font-mono">
                      Nicio secțiune activă în această categorie.
                    </div>
                  ) : activeBoards.map((b) => {
                    const subs = boards.filter((x) => x.parent_id === b.id);
                    return (
                      <div 
                        key={b.id} 
                        className="grid grid-cols-12 gap-4 p-4 hover:bg-[#0c1018] transition-colors group"
                        data-testid={`board-link-${b.id}`}
                      >
                        {/* Board Details Column */}
                        <div className="col-span-12 md:col-span-8 flex flex-col justify-center">
                          {/* Board Name */}
                          <div className="flex items-center gap-2 mb-1">
                            <span 
                              className="w-2 h-2 shrink-0 shadow-sm"
                              style={{ backgroundColor: color }}
                            />
                            <Link 
                              to={`/forums/${b.id}`}
                              className="font-chakra text-sm font-bold uppercase tracking-wider text-white group-hover:text-[var(--tf-primary)] transition-colors"
                            >
                              {b.name}
                            </Link>
                          </div>

                          {/* Board Description */}
                          {b.description && (
                            <p className="text-xs text-zinc-500 font-mono pl-4 mb-2">
                              {b.description}
                            </p>
                          )}

                          {/* Subforums Grid (Respects ACP Column Settings: 1, 2, 3, 4, 5) */}
                          {subs.length > 0 && (() => {
                            const cols = Number(b.subforum_columns) || 3;
                            const gridColsClass = 
                              cols === 1 ? "grid-cols-1" :
                              cols === 2 ? "grid-cols-1 sm:grid-cols-2" :
                              cols === 4 ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4" :
                              cols === 5 ? "grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5" :
                              "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"; // default 3 cols
                            
                            return (
                              <div className={`grid ${gridColsClass} gap-1.5 mt-2 pl-4 max-w-3xl`}>
                                {subs.map((s) => (
                                  <Link
                                    key={s.id}
                                    to={`/forums/${s.id}`}
                                    onClick={(e) => e.stopPropagation()}
                                    className="bg-[#07090e]/90 hover:bg-[#0d121c] border border-[#161c28] hover:border-[var(--tf-primary)]/60 px-2.5 py-1.5 rounded-sm text-xs font-semibold text-zinc-200 hover:text-white flex items-center gap-2 transition-all shadow-sm group/sub"
                                    data-testid={`subforum-link-${s.id}`}
                                  >
                                    <span className="w-1.5 h-1.5 rounded-full bg-[var(--tf-primary)]/80 group-hover/sub:bg-[var(--tf-primary)] group-hover/sub:shadow-[0_0_6px_var(--tf-primary)] shrink-0 transition-all"></span>
                                    <span className="truncate group-hover/sub:text-white transition-colors">{s.name}</span>
                                    <span className="ml-auto text-[10px] font-bold text-zinc-400 bg-black/60 group-hover/sub:bg-[var(--tf-primary)]/20 group-hover/sub:text-white px-1.5 py-0.5 rounded border border-white/[0.05] group-hover/sub:border-[var(--tf-primary)]/40 shrink-0 transition-all">
                                      {s.topic_count || 0}
                                    </span>
                                  </Link>
                                ))}
                              </div>
                            );
                          })()}
                        </div>

                        {/* Last Post Column */}
                        <div className="col-span-12 md:col-span-4 flex items-center">
                          <div className="bg-[#06080d] border border-[#151924] p-2.5 w-full shadow-inner">
                            <LastPostMember
                              user={b.last_topic?.last_post_user}
                              time={b.last_topic?.last_post_at || b.last_topic?.created_at}
                              topicTitle={b.last_topic?.title}
                              topicLink={b.last_topic ? `/topic/${b.last_topic.id}` : null}
                              customColor={color}
                              stats={
                                <div className="flex items-center gap-4 text-[10px] font-mono font-bold text-zinc-500 uppercase tracking-widest pt-1 border-t border-[#141824]">
                                  <span className="flex items-center gap-1">
                                    <FileText className="w-3 h-3 text-zinc-400" />
                                    <span className="text-zinc-300">{b.topic_count || 0}</span> TOPICURI
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <MessageSquare className="w-3 h-3 text-zinc-400" />
                                    <span className="text-zinc-300">{b.post_count || 0}</span> POSTĂRI
                                  </span>
                                </div>
                              }
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}

